/**
 * Dependencies
 */

import React from 'react';

import classnames from 'classnames';

// Utils
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { isEmpty } from 'lodash';

import { useGlobalMarkupOptions } from '@nerdwallet/coin/GlobalMarkupOptions';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import {
  AdvertiserDisclosure,
  EditorialStandards,
  Title,
  Box,
  Text,
} from '@nerdwallet/currency';

// Local Components
import TwoPanelContainer from '../two-panel-container/two-panel-container';

// CSS
import style from './results.module.less';

interface ResultsProps {
  additionalContent?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  controlPanel?: React.ReactNode;
  data?: Record<string, any>;
  fthbNav?: React.ReactNode;
  heading?: string;
  lenders?: React.ReactNode;
  membershipCta?: React.ReactNode;
  methodology?: React.ReactNode;
  relatedArticles?: React.ReactNode;
  resultsPanel?: React.ReactNode;
  split?: string;
  title?: React.ReactNode;
  subcopy?: string;
  toolLinkCards?: React.ReactNode;
}

export const Results: React.FC<ResultsProps> = (props) => {
  const {
    additionalContent,
    className,
    controlPanel,
    children,
    data,
    fthbNav,
    heading,
    lenders,
    membershipCta,
    methodology,
    relatedArticles,
    resultsPanel,
    split,
    title,
    toolLinkCards,
    subcopy,
  } = props;

  const twoPanelProps = {
    className: classnames({ zoomed: data?.zoomed }),
    primaryPanelProps: {
      children: resultsPanel,
    },
    secondaryPanelProps: {
      children: controlPanel,
    },
    split,
  };

  const { pageViewId } = useAnalyticsPageView();

  useGlobalMarkupOptions({
    segmentTrackingAttributes: {
      driverPageViewId: pageViewId,
    },
  });

  // If the data property is empty - the reducer hasn't be set up yet.
  if (isEmpty(data)) {
    return <div />;
  }

  const pageTitle = title || <Title component="h1">{heading}</Title>;
  const withNav = !!fthbNav;
  const contentWidth = withNav ? { desktop: '70', mobile: '100' } : 100;

  return (
    <React.Fragment>
      <div className={style.resultsWrapper}>
        <div className={classnames(style.hacWrapper, className)}>
          <div className={style.hacResultsContainer}>
            <div className={style.advertiserDisclosure}>
              <AdvertiserDisclosure
                enableAnalytics={{ onClick: true, onHover: false }}
              />
            </div>
            <Box paddingY="4">
              {pageTitle}
              {subcopy && (
                <Box marginTop={2}>
                  <Text>{subcopy}</Text>
                </Box>
              )}
            </Box>
            <TwoPanelContainer {...twoPanelProps} />
            {lenders && (
              <EditorialStandards className={style.editorialStandards} />
            )}
            <Box display="flex">
              {/* @ts-ignore - ignore ts error on `width` prop */}
              <Box display="inline-block" width={contentWidth}>
                {lenders}
                <Box marginLeft="2">{membershipCta}</Box>
                {children}
              </Box>
              {withNav && (
                <Box
                  width="30"
                  display={{ desktop: 'inline-block', mobile: 'none' }}
                >
                  {fthbNav}
                </Box>
              )}
            </Box>
          </div>
          {methodology}
        </div>
      </div>
      {relatedArticles}
      {toolLinkCards}
      {additionalContent}
    </React.Fragment>
  );
};

Results.defaultProps = {
  split: '66%',
  additionalContent: null,
  children: null,
  className: null,
  controlPanel: null,
  fthbNav: null,
  heading: null,
  lenders: null,
  membershipCta: null,
  methodology: null,
  relatedArticles: null,
  title: null,
  toolLinkCards: null,
};

export default Results;
